import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, AlertTriangle, Share, Plus, Download } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { usePWAInstall } from "../hooks/usePWAInstall";

function InstallCard({ isIOS, prompt, onInstall }) {
  return (
    <div className="poster-card border-2 border-gold/40 p-6" data-testid="pwa-install-card">
      <div className="font-heading uppercase tracking-widest text-gold text-lg">The Loaded Street Kitchen App</div>
      <p className="mt-2 text-cream/70 text-sm leading-relaxed">
        Get one-tap access to our menu and your orders — no app store needed.
      </p>

      {isIOS ? (
        <div className="mt-4 flex flex-col gap-2 text-sm text-cream/80">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Share className="h-3.5 w-3.5" />
            </span>
            <span>Tap the <strong className="text-cream">Share</strong> button at the bottom of Safari</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span>Tap <strong className="text-cream">Add to Home Screen</strong></span>
          </div>
        </div>
      ) : prompt ? (
        <button
          onClick={onInstall}
          className="mt-5 w-full btn-poster flex items-center justify-center gap-2"
          data-testid="pwa-install-btn"
        >
          <Download className="h-4 w-4" /> Download
        </button>
      ) : (
        <div className="mt-4 flex flex-col gap-2 text-sm text-cream/80">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0 text-xs font-bold">⋮</span>
            <span>Tap the <strong className="text-cream">menu</strong> button in your browser (top right)</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span>Tap <strong className="text-cream">Add to Home Screen</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, msg: "Checking payment…" });
  const [installDone, setInstallDone] = useState(false);
  const attempts = useRef(0);
  const { clear } = useCart();
  const { prompt, install, isInstalled, isIOS, isAndroid } = usePWAInstall();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, msg: "Missing session id." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          clear();
          setState({ phase: "paid", data, msg: "Payment confirmed!" });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, msg: "Payment session expired." });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ phase: "timeout", data, msg: "Still processing — we'll email confirmation." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", data: null, msg: "Could not check payment status." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  const handleInstall = async () => {
    const accepted = await install();
    if (accepted) setInstallDone(true);
  };

  const isMobile = isIOS || isAndroid;
  const showInstallCard = state.phase === "paid" && !isInstalled && !installDone && isMobile;

  return (
    <div className="min-h-screen bg-ink text-cream flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full space-y-4">

        {/* PWA install card — above confirmation */}
        {showInstallCard && (
          <InstallCard isIOS={isIOS} prompt={prompt} onInstall={handleInstall} />
        )}

        <div className="poster-card p-8 text-center" data-testid="checkout-success-card">
          {state.phase === "polling" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-gold animate-spin" />
              <h1 className="mt-6 font-heading uppercase tracking-tight text-cream text-4xl">{state.msg}</h1>
            </>
          )}
          {state.phase === "paid" && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-gold" />
              <h1 className="mt-6 font-heading uppercase tracking-tight text-cream text-5xl" data-testid="success-title">Order Locked In</h1>
              <p className="mt-3 text-cream/75">
                We've got your order. Hot food en route — within our 2.5 mile delivery zone.
              </p>
              {state.data?.order && (
                <div className="mt-6 text-left border border-border p-4">
                  <div className="font-heading text-gold uppercase tracking-widest text-sm">Order Summary</div>
                  <ul className="mt-3 space-y-1 text-sm">
                    {state.data.order.items.map((it, i) => (
                      <li key={i} className="flex justify-between text-cream/80">
                        <span>{it.quantity}× {it.name}</span>
                        <span>£{it.line_total.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 border-t border-border pt-2 flex justify-between font-heading text-cream">
                    <span>Total</span>
                    <span className="text-gold">£{state.data.order.total_gbp.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </>
          )}
          {(state.phase === "error" || state.phase === "expired" || state.phase === "timeout") && (
            <>
              <AlertTriangle className="h-12 w-12 mx-auto text-accent" />
              <h1 className="mt-6 font-heading uppercase tracking-tight text-cream text-4xl">{state.msg}</h1>
            </>
          )}
          <Link to="/" className="mt-8 inline-block btn-outline-poster" data-testid="back-home-btn">
            Back to Menu
          </Link>
        </div>

        {/* PWA install card — below confirmation */}
        {showInstallCard && (
          <InstallCard isIOS={isIOS} prompt={prompt} onInstall={handleInstall} />
        )}

        {installDone && (
          <div className="poster-card border border-green-500/40 p-4 text-center text-green-400 font-heading uppercase tracking-widest text-sm">
            Added to your home screen
          </div>
        )}

      </div>
    </div>
  );
}
